"use client";

import { Suspense, useMemo } from "react";

import { usePathname } from "next/navigation";
import { getWorkloadInISO } from "@/helpers/get-workload-iso";
import { Section } from "@/components/section";
import { SeoHeading } from "@/components/common/seo-heading";
import { Text } from "@/components/ui/text";
import { StructuredData } from "@/components/common/structured-data";
import { FastFillingEvent } from "@/components/common/fast-filling-event";
import { DownloadBrochureCard } from "@/components/common/download-brochure-card";

type AboutCourseProps = {
  data: {
    seoHeading?: string;
    heading?: string;
    description?: string;
    designation?: string;
    event?: ServiceEvent;
    category?: string;
    serviceId: number;
    serviceTrainingDays?: string;
    reviews?: {
      name?: string;
      rating?: number;
      comment?: string;
      publishAt?: string;
    }[];
  };
  section: PageSections;
};

const AboutCourse = ({ data, section }: AboutCourseProps) => {
  const pathname = usePathname();
  const courseInfoSchema = useMemo(() => {
    const ratingCount = data?.reviews?.length || 0;
    const total =
      data?.reviews?.reduce((sum, r) => sum + parseInt(r.rating?.toString() || "0"), 0) || 0;
    const ratingValue = ratingCount ? total / ratingCount : 0;

    const headingText = data?.seoHeading?.trim() || data?.heading?.trim();
    return {
      "@context": "https://schema.org",
      "@id": process.env.NEXT_PUBLIC_APP_URL + pathname,
      "@type": "Course",
      name: headingText ?? "Course Title Not Available",
      description: data?.description?.trim() || headingText || "description not yet created",
      category: data?.category?.trim() ?? "",
      publisher: {
        "@type": "Organization",
        name: "Spoclearn",
        url: process.env.NEXT_PUBLIC_APP_URL,
      },
      provider: {
        "@type": "Organization",
        name: "Spoclearn",
        sameAs: process.env.NEXT_PUBLIC_APP_URL,
      },
      image: process.env.NEXT_PUBLIC_APP_URL + "/images/logo.svg",
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: data?.event?.modeOfTraining === "Live Online Classroom" ? "Online" : "Blended",
        location: "Spoclearn",
        courseSchedule: {
          "@type": "Schedule",
          duration: getWorkloadInISO(data.serviceTrainingDays || "16 Hrs"),
          repeatFrequency: "Daily",
          repeatCount: 5,
          startDate: data?.event?.startDate || "2025-01-01",
          endDate: data?.event?.endDate || "2025-12-31",
        },
      },
      review:
        data?.reviews?.map((review) => ({
          "@type": "Review",
          author: {
            "@type": "Person",
            name: review.name || "Anonymous",
          },
          reviewBody: review.comment || "No comment provided",
          reviewRating: {
            "@type": "Rating",
            ratingValue: review.rating || "5",
            bestRating: review.rating || "5",
          },
          datePublished: review.publishAt || "2025-07-15",
        })) || [],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: ratingValue || 4.5,
        bestRating: total || 5,
        ratingCount: ratingCount || 25,
      },
      offers: {
        "@type": "Offer",
        url: process.env.NEXT_PUBLIC_APP_URL + pathname,
        priceCurrency: data?.event?.eventDetail?.currency,
        price: data?.event?.eventDetail?.price,
        availability: "https://schema.org/InStock",
        validFrom: data.event?.startDate,
        category: data?.category,
      },
    };
  }, [data, pathname]);
  return (
    <Section id={section.sectionId} className={section.sectionClass} data-title={section.name}>
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 md:col-span-12">
          <SeoHeading seoHeading={data.seoHeading} heading={data.heading} as="h2" />
          <Text className="[&_a]:text-primary [&_a]:hover:underline [&_p]:mb-4 [&_p:last-child]:mb-0">
            {data.description}
          </Text>
        </div>
        {/* <div className="col-span-12 md:col-span-4">
          <Suspense>
            <span className="sticky top-32">
              <FastFillingEvent data={data.event} />
              <DownloadBrochureCard serviceId={data.serviceId} />
            </span>
          </Suspense>
          <StructuredData data={courseInfoSchema} />
        </div> */}
      </div>
    </Section>
  );
};

export default AboutCourse;
